/**
 * scenarioService.js
 *
 * AI 시나리오 플래닝 REST API 서비스.
 *
 * 호출 경로:
 *   Frontend
 *     → Java 백엔드 프록시  /api/v1/scenario/{module}/*
 *     → Python FastAPI (saas-ai-agent)  /scenario/{module}/*
 *
 * ★ URL 규칙 (SKILL.md §9.1):
 *   suggest     → POST   /api/v1/scenario/{module}/suggest
 *   getResult   → GET    /api/v1/scenario/{module}/result/{requestId}
 *   analyze     → POST   /api/v1/scenario/{module}/analyze
 *   confirm     → POST   /api/v1/scenario/{module}/confirm
 *   apply       → POST   /api/v1/scenario/{module}/apply
 *   chat        → POST   /api/v1/scenario/{module}/chat
 */

import axios from "axios";
import axiosUtil from "@plannel/utils/axios-bigint";
import authService from "@plannel/services/system/auth-service";
import { SESSION_EXPIRED_EVENT } from "@plannel/services/utils/rest-api";
import { getAgentLanguage } from "@plannel/utils/i18n";

// 401 알림 중복 방지 — restApi 와 같은 규약(2xx 가 오면 다시 무장한다).
//   ⚠️ 이 서비스는 restApi 가 아니라 자체 axios 인스턴스라 rest-api.js 의 인터셉터가 걸리지
//      않는다. 그래서 30분 결과 폴링 중 토큰이 만료되면 요청이 조용히 401 로 죽고 화면은
//      "엔진 실패"처럼 보였다. 이벤트만 같은 것을 쏘고 처리는 App.js 가 한다.
let _sessionExpiredNotified = false;

/** 공통 Axios 인스턴스 (모듈 코드는 URL에 포함) */
const _createInstance = () => {
  const user = authService.getCurrentUser();
  return axios.create({
    headers: user && user.accessToken
      ? {
          "Content-type": "application/json",
          "x-tenant-id": user.tenantId,
          Authorization: `${user.type} ${user.accessToken}`,
        }
      : { "Content-type": "application/json" },
    transformResponse: axiosUtil.transformResponse,
    transformRequest: axiosUtil.transformRequest,
    timeout: 300000, // 5분 (시나리오 엔진 실행 대기)
  });
};

/** 401 → 세션 만료 이벤트. restApi(services/utils/rest-api.js)와 같은 규약. */
const _withAuthGuard = (instance) => {
  instance.interceptors.response.use(
    (response) => { _sessionExpiredNotified = false; return response; },
    (error) => {
      // 403 은 제외한다 — 로그인은 유효하고 권한만 없는 것이라 재로그인 대상이 아니다.
      if (error?.response?.status === 401 && !_sessionExpiredNotified) {
        _sessionExpiredNotified = true;
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      return Promise.reject(error);   // 화면별 에러 처리는 그대로 둔다
    },
  );
  return instance;
};

/** 모듈 코드 → lowercase (URL 경로용) */
const BASE = "/api/v1/scenario";
const _mod = (mod) => mod.toLowerCase();

// SSE 경로의 JSON.stringify 는 BigInt 를 직렬화 못함(마케팅 신호 ID 등 snowflake) → 문자열로 변환.
const _bigintReplacer = (_k, v) => (typeof v === "bigint" ? v.toString() : v);
const _sseStringify = (body) => JSON.stringify(body, _bigintReplacer);

// ── SSE 공용 (suggest / analyze / chat) ───────────────────────────────────────
// EventSource는 커스텀 헤더(Authorization·x-tenant-id)를 지원하지 않으므로
// fetch + ReadableStream으로 SSE를 직접 파싱한다.

const _sseHeaders = () => {
  const user = authService.getCurrentUser();
  const headers = { "Content-type": "application/json", Accept: "text/event-stream" };
  if (user && user.accessToken) {
    headers["x-tenant-id"] = user.tenantId;
    headers.Authorization = `${user.type} ${user.accessToken}`;
  }
  return headers;
};

/** SSE 본문을 읽어 이벤트마다 onEvent(evt)를 호출. 스트림 종료 시 resolve. */
const _readSseStream = async (res, onEvent) => {
  const reader  = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";

  // SSE 이벤트는 빈 줄("\n\n")로 구분, 각 줄의 "data:" 접두 후 JSON
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buf.indexOf("\n\n")) >= 0) {
      const rawEvent = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataStr = rawEvent
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trim())
        .join("");
      if (!dataStr) continue;
      let evt;
      try { evt = JSON.parse(dataStr); } catch { continue; }
      onEvent?.(evt);
    }
  }
};

/** SSE 엔드포인트를 열고 끝까지 읽는다. */
const _openSseStream = async (path, body, { onEvent, signal } = {}) => {
  const res = await fetch(path, {
    method: "POST",
    headers: _sseHeaders(),
    body: _sseStringify(body),
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`SSE 연결 실패: HTTP ${res.status}`);
  // ⚠️ res.ok 만으로는 부족하다. 토큰이 만료되면 서버가 /home 리다이렉트로 응답을 커밋하고
  //    fetch 가 그것을 추종해 결국 200 + index.html(HTML)을 받는다 — ok 검사를 통과한 채
  //    이벤트 0건으로 조용히 끝난다. content-type 으로 걸러 원인을 드러낸다.
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("text/event-stream")) {
    throw new Error(`SSE 응답이 아님 (content-type: ${ct || "없음"}) — 세션 만료 가능`);
  }
  await _readSseStream(res, onEvent);
};

// ── API 함수 ──────────────────────────────────────────────────────────────────

/**
 * Step 1: 현황 분석 → 시나리오 3개 제안 시작 (비동기)
 * POST /api/v1/scenario/{module}/suggest
 * @param {string} mod     - 모듈 코드 (DP | IP | RP | MP)
 * @param {object} payload - { planVerId, locCd, constraints, scenarioCount, language }
 * @returns {{ request_id }}
 */
const suggest = async (mod, payload) => {
  const body = {
    plan_ver_id:    payload.planVerId ?? null,
    loc_cd:         payload.locCd ?? null,
    constraints:    payload.constraints ?? { target_achievement_pct: 95 },
    scenario_count: payload.scenarioCount ?? 3,
    language:       payload.language ?? getAgentLanguage(),
    planning_range: payload.planningRange ?? 18,
    user_id:        payload.userId ?? 0,
    // BF 전용: 마케팅 신호 ID 목록 (빈 배열 = 전체 APPROVED 자동 조회)
    signal_ids:     payload.signalIds ?? [],
    // MP 전용: 현황 분석 권장 대책 — 있으면 시나리오 생성 근거로 전달(없으면 키 없음)
    ...(payload.recommendedActions ? { recommended_actions: payload.recommendedActions } : {}),
    // 🔴 FP 전용: FP 는 plan_ver_id 로 계획을 가리킬 수 없고 (계획범위, 버전코드) **한 쌍**이 필요하다.
    //    없으면 resolve_fp_target 이 fail-fast 해 AI 추천 경로가 통째로 죽는다 [Q-031].
    //    analyze 계열에는 원래 있었고 suggest 계열에만 빠져 있었다. 값이 없으면 키를 안 넣으므로 타 모듈 무영향.
    ...(payload.planScope ? { plan_scope: payload.planScope } : {}),
    ...(payload.sourceVersionCd ? { source_version_cd: payload.sourceVersionCd } : {}),
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/suggest`, body);
  return res.data;
};

/**
 * Step 1(스트리밍): AI 추천 진행 상황을 SSE로 실시간 수신
 * POST /api/v1/scenario/{module}/suggest/stream
 *
 * EventSource는 커스텀 헤더(Authorization·x-tenant-id)를 지원하지 않으므로
 * fetch + ReadableStream으로 SSE를 직접 파싱한다.
 *
 * @param {string} mod      - 모듈 코드 (BF | DP | IP | RP | MP)
 * @param {object} payload  - suggest()와 동일한 입력
 * @param {object} handlers - { onEvent(evt), signal }
 *   evt.type: "start" | "tool_start" | "tool_done" | "result" | "error" | "done"
 *   RP는 result 이벤트로 결과를 직접 전달, 그 외 모듈은 done 후 호출측이 getResult로 회수
 * @returns {Promise<void>} 스트림 종료 시 resolve
 */
const suggestStream = async (mod, payload, { onEvent, signal } = {}) => {
  const body = {
    plan_ver_id:    payload.planVerId ?? null,
    loc_cd:         payload.locCd ?? null,
    constraints:    payload.constraints ?? { target_achievement_pct: 95 },
    scenario_count: payload.scenarioCount ?? 3,
    language:       payload.language ?? getAgentLanguage(),
    planning_range: payload.planningRange ?? 18,
    user_id:        payload.userId ?? 0,
    signal_ids:     payload.signalIds ?? [],
    // MP 전용: 현황 분석 권장 대책 — 있으면 시나리오 생성 근거로 전달(없으면 키 없음)
    ...(payload.recommendedActions ? { recommended_actions: payload.recommendedActions } : {}),
    // 🔴 FP 전용 — 위 suggest() 와 같은 이유다 [Q-031]. 두 곳을 함께 고쳐야 한다
    //    (SSE 실패 시 프런트가 비스트림으로 폴백하므로 한쪽만 고치면 폴백에서 다시 죽는다).
    ...(payload.planScope ? { plan_scope: payload.planScope } : {}),
    ...(payload.sourceVersionCd ? { source_version_cd: payload.sourceVersionCd } : {}),
  };
  await _openSseStream(`${BASE}/${_mod(mod)}/suggest/stream`, body, { onEvent, signal });
};

/**
 * Step 2(스트리밍): 엔진 실행 + KPI 비교 진행 상황을 SSE로 실시간 수신
 * POST /api/v1/scenario/{module}/analyze/stream
 *
 * suggestStream과 동일한 SSE 파싱 구조. 차이점은 엔드포인트(analyze/stream)와
 * body(사용자 확정 scenarios 포함)이며, 격리 버전 생성·엔진 실행까지 수행하므로
 * 단계 이벤트가 수 분에 걸쳐 흘러온다. 최종 결과는 백엔드가 DB에 저장하므로
 * 스트림 종료 후 호출측은 getResult 폴백으로 결과를 회수한다.
 *
 * @param {string} mod      - 모듈 코드 (RP 등)
 * @param {object} payload  - analyze()와 동일 입력 { planVerId, scenarios, constraints, language }
 * @param {object} handlers - { onEvent(evt), signal }
 *   evt.type: "start" | "tool_start" | "tool_done" | "error" | "done"
 * @returns {Promise<void>} 스트림 종료 시 resolve
 */
const analyzeStream = async (mod, payload, { onEvent, signal } = {}) => {
  const body = {
    plan_ver_id:    payload.planVerId ?? null,
    scenario_count: (payload.scenarios?.length) || payload.scenarioCount || 3,
    constraints:    payload.constraints ?? {},
    language:       payload.language ?? getAgentLanguage(),
    scenarios:      payload.scenarios ?? [],
    // FP 전용: 계획범위·기준 버전 — FP는 MP처럼 활성 버전을 자동 유도할 수 없다(z행 부재).
    //   없으면 키를 넣지 않는다(타 모듈 무영향). 수신은 fp/orchestrator.resolve_fp_target.
    ...(payload.planScope ? { plan_scope: payload.planScope } : {}),
    ...(payload.sourceVersionCd ? { source_version_cd: payload.sourceVersionCd } : {}),
  };
  await _openSseStream(`${BASE}/${_mod(mod)}/analyze/stream`, body, { onEvent, signal });
};

/**
 * BF 전용: 현황 조회 통합 API
 * GET /api/v1/scenario/bf/initial-state
 * current_state + item_group_analysis + approved_signals 한 번에 반환
 */
const getBfInitialState = async () => {
  const res = await _createInstance().get(`${BASE}/bf/initial-state`);
  return res.data;
};

/**
 * DP 전용: 현황 조회
 * GET /api/v1/scenario/dp/initial-state
 * latest released DP plan version의 KPI 현황 + 제품군별 분석 반환
 */
const getDpInitialState = async () => {
  const res = await _createInstance().get(`${BASE}/dp/initial-state`);
  return res.data;
};

/**
 * IP 전용: 현황 조회
 * GET /api/v1/scenario/ip/initial-state
 * latest released IP plan version의 재고 현황 KPI 반환
 */
const getIpInitialState = async () => {
  const res = await _createInstance().get(`${BASE}/ip/initial-state`);
  return res.data;
};

/**
 * RP 전용: 현황 조회
 * GET /api/v1/scenario/rp/initial-state
 * latest RELEASED RP plan version의 보충계획 KPI + 이슈 + 긴급 품목 반환
 */
const getRpInitialState = async () => {
  const res = await _createInstance().get(`${BASE}/rp/initial-state`);
  return res.data;
};

/**
 * RP 전용: 표준 시나리오 카탈로그 조회 (z_ai_scenario 기반 — 화면 하드코딩 대체)
 * GET /api/v1/scenario/rp/scenarios
 * @returns {{ module, plan_ver_id, plan_ver_name, baseline, scenarios: [
 *   { group_cd, scenario_cd, scenario_name, desc_txt, issue_situation, recommended_flg,
 *     param_defs, variants: [base, alt1, alt2] } ] }}
 */
const getRpScenarios = async () => {
  const res = await _createInstance().get(`${BASE}/rp/scenarios`);
  return res.data;
};

/**
 * 제네릭: 모듈별 표준 시나리오 카탈로그 조회 (z_ai_scenario 기반 — 화면 하드코딩 대체)
 * GET /api/v1/scenario/{module}/scenarios  (BF | DP | IP | MP | FP)
 * RP 는 baseline/param_defs 포함 전용 getRpScenarios() 사용.
 *
 * ⚠️ FP만 기준 계획(계획범위+버전)을 지목해야 한다 — baseline("현재값")과 scope_targets가 그
 *   계획의 결과에서 나오기 때문이다(D-031: FP 일반 버전엔 z_plan_version 행이 없어 plan_ver_id로
 *   짚을 수 없고 키가 (plan_scope, version_cd) 문자열이다). 서버는 literal /fp/scenarios 라우트가
 *   받는다. 값이 없으면 키를 넣지 않고(타 모듈 무영향) 서버가 빈 카탈로그를 돌려준다.
 *   🔴 FP 전용 함수를 따로 두지 않는 이유: ScenarioManage 화면이 이미 getScenarios("FP")를
 *     부르므로 경로가 둘로 갈리면 그 화면만 영구히 실패한다 — analyzeStream 주석과 같은 실패 형태다.
 *
 * @param {string} mod  - 모듈 코드 (BF | DP | IP | MP | FP)
 * @param {object} opts - { planScope?, versionCd? } — FP 전용, 그 외 모듈은 생략한다
 * @returns {{ module, scenarios: [
 *   { scenario_cd, scenario_name, scenario_direction, desc_txt, recommended_flg,
 *     labels: { master_data_changes, master_data_changes_display, policy_changes },
 *     variants: [base, alt1, alt2] } ] }}
 */
const getScenarios = async (mod, opts = {}) => {
  const params = {
    ...(opts.planScope ? { planScope: opts.planScope } : {}),
    ...(opts.versionCd ? { versionCd: opts.versionCd } : {}),
  };
  const res = await _createInstance().get(`${BASE}/${_mod(mod)}/scenarios`, { params });
  return res.data;
};

/**
 * 커스텀(사용자) 시나리오 생성 (동기): 자유 텍스트 → 구조화 시나리오 초안
 * POST /api/v1/scenario/{module}/custom
 * @param {string} mod     - 모듈 코드 (BF | DP | IP | RP | MP)
 * @param {object} payload - { situation, referenceScenario?, paramCatalog?, language? }
 * @returns {{ scenario_name:{value,confidence}, scenario_direction, desc_txt, issue_situation,
 *            trigger_keywords:{value:[],confidence}, variant_desc, params:{[k]:{value,confidence}},
 *            scope_type, scope_options }}
 */
const customGenerate = async (mod, payload) => {
  const body = {
    situation:          payload.situation ?? "",
    reference_scenario: payload.referenceScenario ?? null,
    param_catalog:      payload.paramCatalog ?? [],
    language:           payload.language ?? getAgentLanguage(),
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/custom`, body, { timeout: 120000 });
  return res.data;
};

/**
 * 커스텀(사용자) 시나리오 저장 — scenario_cd 없으면 신규 등록, 있으면 수정
 * POST /api/v1/scenario/{module}/custom-save
 * @param {string} mod     - 모듈 코드
 * @param {object} payload - { scenarioCd?, scenarioName, scenarioDirection, descTxt,
 *                             issueSituation, triggerKeywords: [], variantDesc, params: {k:v} }
 * @returns {{ saved, scenario_cd, mode }}
 */
const saveCustomScenario = async (mod, payload) => {
  const body = {
    scenario_cd:        payload.scenarioCd ?? "",
    scenario_name:      payload.scenarioName ?? "",
    scenario_direction: payload.scenarioDirection ?? "",
    desc_txt:           payload.descTxt ?? "",
    issue_situation:    payload.issueSituation ?? "",
    trigger_keywords:   payload.triggerKeywords ?? [],
    variant_name:       payload.variantName ?? payload.scenarioName ?? "",
    variant_desc:       payload.variantDesc ?? "",
    scope_type:         payload.scopeType ?? "all",
    params:             payload.params ?? {},
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/custom-save`, body);
  return res.data;
};

/**
 * 커스텀(사용자) 시나리오 목록 조회
 * GET /api/v1/scenario/{module}/custom-scenarios  (module='all' 이면 전 모듈)
 * @returns {{ module, scenarios: [{ module_cd, scenario_cd, scenario_name, scenario_direction,
 *             desc_txt, issue_situation, trigger_keywords, variant_desc, params, created_ts }] }}
 */
const listCustomScenarios = async (mod = "all") => {
  const res = await _createInstance().get(`${BASE}/${_mod(mod)}/custom-scenarios`);
  return res.data;
};

/**
 * 커스텀(사용자) 시나리오 삭제 (source_type='USER' 행만)
 * DELETE /api/v1/scenario/{module}/custom-scenarios/{scenarioCd}
 */
const deleteCustomScenario = async (mod, scenarioCd) => {
  const res = await _createInstance().delete(
    `${BASE}/${_mod(mod)}/custom-scenarios/${encodeURIComponent(scenarioCd)}`);
  return res.data;
};

/**
 * 시나리오 즐겨찾기 목록 조회 (로그인 사용자 기준)
 * GET /api/v1/scenario/{module}/favorites  (module='all' 이면 전 모듈)
 *
 * 즐겨찾기는 per-user 상태라 카탈로그(getScenarios)와 분리된 리소스다 — 카탈로그 응답은
 * 테넌트 공용이며 화면 캐시·LLM 프롬프트로 흘러가므로 사용자별 값을 섞지 않는다.
 * @returns {string[]} scenario_cd 배열 (표준/사용자 시나리오 구분 없음)
 */
const listFavoriteScenarios = async (mod = "all") => {
  const res = await _createInstance().get(`${BASE}/${_mod(mod)}/favorites`);
  return res.data?.scenario_cds ?? [];
};

/**
 * 시나리오 즐겨찾기 설정/해제
 * PUT /api/v1/scenario/{module}/favorites/{scenarioCd}
 *
 * 토글이 아니라 목표 상태를 보내는 멱등 호출 — 화면이 낙관적 갱신을 하므로 중복·재시도
 * 요청이 반대 상태로 착지하면 별표와 DB 가 어긋난다.
 * @returns {{ scenario_cd, favorite }}
 */
const setFavoriteScenario = async (mod, scenarioCd, favorite) => {
  const res = await _createInstance().put(
    `${BASE}/${_mod(mod)}/favorites/${encodeURIComponent(scenarioCd)}`,
    { favorite: !!favorite });
  return res.data;
};

/**
 * MP 전용: 현황 조회
 * GET /api/v1/scenario/mp/initial-state
 * latest RELEASED/PLANNED MP plan version의 생산계획 KPI 반환
 *
 * @param {boolean} genSummary true 이면 LLM 현황 요약(status_narrative)을 함께 요청.
 *   생성 실패 시 status_narrative 가 빠진 채로 KPI 본문만 반환된다.
 */
// in-flight 공유 — 동일 (언어, genSummary) 호출이 거의 동시에 오면(현황 분석 탭 + 시나리오 구성 탭이
//   각자 호출) 같은 Promise 를 돌려줘 요청·LLM 중복을 막는다. 완료 후 제거해 다음 호출은 새로 조회.
const _mpInitialStateInflight = {};
const getMpInitialState = (genSummary = false) => {
  const lang = getAgentLanguage();
  const key = `${lang}:${genSummary ? 1 : 0}`;
  if (_mpInitialStateInflight[key]) return _mpInitialStateInflight[key];
  const p = _createInstance()
    .get(`${BASE}/mp/initial-state`, {
      params: { language: lang, ...(genSummary ? { genSummary: true } : {}) },
    })
    // 응답에 생성 언어를 새긴다 — 화면이 캐시된 LLM 요약을 현재 언어와 대조해 재조회할 수 있게.
    .then(res => ({ ...res.data, _lang: lang }))
    .finally(() => { delete _mpInitialStateInflight[key]; });
  _mpInitialStateInflight[key] = p;
  return p;
};

/**
 * FP 전용: 현황 조회
 * GET /api/v1/scenario/fp/initial-state
 *
 * ⚠️ FP만 기준 계획(계획범위+버전)을 지목해야 한다 — 타 5모듈은 서버가 최신 릴리즈를 자동 유도하지만
 *   FP 일반 버전엔 z_plan_version 행이 없어 plan_ver_id로 짚을 수 없고 키가 (plan_scope, version_cd)
 *   문자열이다(D-031). 값이 없으면 FastAPI가 400을 내고 @ControllerAdvice가 없어 클라이언트엔 500으로
 *   보이므로, 호출자는 둘이 모두 정해진 뒤에만 부른다. 파라미터명은 getScenarios와 같은 camelCase다
 *   (Java @RequestParam이 camelCase — ScenarioAiV1Controller.fpInitialState).
 *
 * 🔴 in-flight 키에 planScope/versionCd가 반드시 들어간다 — MP처럼 (언어, genSummary)로만 키잉하면
 *   서로 다른 기준 계획의 조회가 한 Promise로 합쳐져 **엉뚱한 버전의 현황이 반환된다**. FP는 사용자가
 *   화면 안에서 버전을 바꿀 수 있어 MP에 없는 위험이다.
 *
 * ⚠️ 서버에는 TTL 캐시도 in-flight dedup도 없다(scenario_fp.py가 의도적 미도입을 명시). 여기 공유는
 *   **동시 호출**만 막고 재방문은 못 막는다 — 재방문은 화면의 부모 캐시(agentResults.FP.initialState)가
 *   담당한다. genSummary=true는 Bedrock 동기 호출이라 특히 중요하다.
 *
 * @param {object} opts - { planScope, versionCd, genSummary? }
 * @returns {{ module, plan_scope, version_cd, kpi_keys, kpi_current, kpi_current_display,
 *   current_state, problem_evidence, detected_issues, status_narrative? }}
 */
const _fpInitialStateInflight = {};
const getFpInitialState = ({ planScope, versionCd, genSummary = false } = {}) => {
  const lang = getAgentLanguage();
  const key = `${lang}:${planScope}:${versionCd}:${genSummary ? 1 : 0}`;
  if (_fpInitialStateInflight[key]) return _fpInitialStateInflight[key];
  const p = _createInstance()
    .get(`${BASE}/fp/initial-state`, {
      params: {
        planScope, versionCd, language: lang,
        ...(genSummary ? { genSummary: true } : {}),
      },
    })
    // 응답에 생성 언어를 새긴다 — 화면이 캐시된 LLM 요약을 현재 언어와 대조해 재조회할 수 있게(MP와 동일).
    .then(res => ({ ...res.data, _lang: lang }))
    .finally(() => { delete _fpInitialStateInflight[key]; });
  _fpInitialStateInflight[key] = p;
  return p;
};

/**
 * BF 전용: 승인된 마케팅 신호 목록 조회
 * GET /api/v1/scenario/bf/signals
 */
const getBfApprovedSignals = async () => {
  const res = await _createInstance().get(`${BASE}/bf/signals`);
  return res.data;
};

/**
 * BF 전용: 외부 인자 미리보기 (출처·과거값 + base 예측 구간 버킷)
 * GET /api/v1/scenario/bf/factor-preview?factors=oil,raw
 * 응답: { factors:[...], period:{ start_date, bucket_unit, buckets, dates:[...] } }
 */
const getBfFactorPreview = async (factors) => {
  const csv = Array.isArray(factors) ? factors.join(",") : (factors || "");
  const res = await _createInstance().get(`${BASE}/bf/factor-preview`, {
    params: { factors: csv },
  });
  return res.data;
};

/**
 * BF 전용: 현황 문제점·원인 AI 분석 (lazy)
 * GET /api/v1/scenario/bf/issue-analysis?planVerId=
 * 응답: { available, plan_ver_id, summary, issues:[{severity,title,cause,recommendation,evidence}], source }
 */
const getBfIssueAnalysis = async (planVerId) => {
  const res = await _createInstance().get(`${BASE}/bf/issue-analysis`, {
    params: { language: getAgentLanguage(), ...(planVerId != null ? { planVerId } : {}) },
  });
  return res.data;
};

/**
 * Step 2: 엔진 실행 + KPI 비교 시작 (비동기)
 * POST /api/v1/scenario/{module}/analyze
 * @param {string} mod     - 모듈 코드
 * @param {object} payload - { planVerId, scenarios, constraints, language }
 * @returns {{ request_id }}
 */
const analyze = async (mod, payload) => {
  const body = {
    plan_ver_id:    payload.planVerId ?? null,
    scenarios:      payload.scenarios ?? [],
    constraints:    payload.constraints ?? { target_achievement_pct: 95 },
    language:       payload.language ?? getAgentLanguage(),
    // FP 전용 — analyzeStream 과 동일. SSE 실패 시 이 폴링 경로로 오므로 양쪽 다 넣어야 한다
    //   (한쪽만 넣으면 정상 경로는 되고 폴백에서만 조용히 스코프가 사라진다).
    ...(payload.planScope ? { plan_scope: payload.planScope } : {}),
    ...(payload.sourceVersionCd ? { source_version_cd: payload.sourceVersionCd } : {}),
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/analyze`, body);
  return res.data;
};

/**
 * 비교 탭: 두 시나리오 버전의 품목 단위 diff 조회 (동기 — 빠른 SQL 집계)
 * POST /api/v1/scenario/{module}/compare/item-facts  (IP | RP | MP | BF)
 * @param {string} mod     - 모듈 코드
 * @param {object} payload - { scenarioVersions: [{role,name,plan_ver_id}],
 *                             baseRole, compareRole, itemCd?, locCd?, topN? }
 * @returns {{ status, base, compare, diff: { worsened, improved, worsened_total,
 *             improved_total, unchanged_total, only_in_base, only_in_compare,
 *             risk_transitions }, meta }}
 */
const compareItemFacts = async (mod, payload) => {
  const body = {
    scenario_versions: payload.scenarioVersions ?? [],
    base_role:         payload.baseRole ?? "base",
    compare_role:      payload.compareRole ?? "",
    item_cd:           payload.itemCd ?? "",
    loc_cd:            payload.locCd ?? "",
    top_n:             payload.topN ?? 10,
    // 비교 탭 정렬·필터 — 전체 매칭 집합에서 Top-N 재선정(서버 슬라이스 전 적용)
    sort_metric:       payload.sortMetric ?? "",
    risk_filter:       payload.riskFilter ?? "",
    loc_filter:        payload.locFilter ?? "",
    // BF(role 기반) — 저장된 분석 결과 조회용 요청 ID (미지정 시 서버가 최신 완료 건)
    request_id:        payload.requestId ?? "",
  };
  const res = await _createInstance().post(
    `${BASE}/${_mod(mod)}/compare/item-facts`, body, { timeout: 60000 });
  return res.data;
};

/**
 * Polling: 진행 상태·결과 조회
 * GET /api/v1/scenario/{module}/result/{requestId}
 * @param {string} mod       - 모듈 코드
 * @param {string} requestId - suggest/analyze에서 반환된 request_id
 * @returns {ScenarioStatusResponse}
 */
const getResult = async (mod, requestId) => {
  // ⚠️ 인스턴스 기본 5분이 아니라 **요청당 30초**다. 결과 회수는 DB 한 건 조회라 30초면
  //    충분하고, 폴링 루프에서 한 폴이 5분을 붙들면 그동안 진행률이 멈춘 것처럼 보인다
  //    (그리고 상한이 벽시계라 그만큼 실제 대기 여유가 줄어든다).
  const res = await _withAuthGuard(_createInstance())
    .get(`${BASE}/${_mod(mod)}/result/${requestId}`, { timeout: 30000 });
  return res.data;
};

/**
 * Step 4: 시나리오 확정
 * POST /api/v1/scenario/{module}/confirm
 * @param {string} mod     - 모듈 코드
 * @param {object} payload - { requestId, scenarioId, planVerId }
 */
const confirm = async (mod, payload) => {
  const body = {
    request_id:  payload.requestId,
    scenario_id: payload.scenarioId,
    plan_ver_id: payload.planVerId ?? null,
    apply_target: payload.applyTarget ?? "scenario_version",
    // IP/RP/MP: 확정 시 버전명/설명 (백엔드가 선택 버전을 PLANNED 로 격상하며 개명).
    // release_name 을 보내지 않으면 백엔드가 정규 Naming Rule 로 버전명을 생성한다.
    ...(payload.releaseName != null ? { release_name: payload.releaseName } : {}),
    ...(payload.releaseDesc != null ? { release_desc: payload.releaseDesc } : {}),
    // DP: 확정 폼에서 조정한 버전 설정(초기값/계획구간/버킷/시작일)
    ...(payload.planConfig != null ? { plan_config: payload.planConfig } : {}),
    // BF 외부 인자 재구성 충돌 처리: "overwrite"(덮어쓰기) | "add"(새 컬럼 추가)
    ...(payload.factorMode != null ? { factor_mode: payload.factorMode } : {}),
    // BF 확정 확인창에서 편집한 인자 이름 {key: name} — 있으면 확인창 통과로 간주(적재 진행)
    ...(payload.factorNames != null ? { factor_names: payload.factorNames } : {}),
    // 확정한 로그인 사용자 — 적재 레코드 생성자/수정자(created_by/updated_by)로 기록
    user_id: payload.userId ?? (() => {
      const u = authService.getCurrentUser();
      return u?.userId ?? u?.id ?? 0;
    })(),
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/confirm`, body);
  return res.data;
};

/**
 * BF 전용: 선택 인자로 대표 품목 실제 시뮬레이션 KPI 비교 (동기, 다소 오래 걸림)
 * POST /api/v1/scenario/bf/compare
 * @param {object} payload - { requestId, scenarioId, featureNames?, testMonths?, itemLimit? }
 * @returns {{ avg_baseline_mape, avg_feature_mape, mape_improvement, items[], ... }}
 */
const compareBfFeatures = async (payload) => {
  const body = {
    request_id:    payload.requestId,
    scenario_id:   payload.scenarioId,
    feature_names: payload.featureNames ?? null,
    test_months:   payload.testMonths ?? 6,
    item_limit:    payload.itemLimit ?? 3,
  };
  const res = await _createInstance().post(`${BASE}/bf/compare`, body, { timeout: 200000 });
  return res.data;
};

/**
 * DP 전용: 프론트가 생성한 버전에 AI 변환(하향/제외) 사후 적용 + 정리 + 확정 (C1)
 * POST /api/v1/scenario/{module}/apply-transform
 * @param {string} mod     - 모듈 코드 (DP)
 * @param {object} payload - { requestId, scenarioId, planVerId }
 */
const applyTransform = async (mod, payload) => {
  const body = {
    request_id:  payload.requestId,
    scenario_id: payload.scenarioId,
    plan_ver_id: payload.planVerId,
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/apply-transform`, body);
  return res.data;
};

/**
 * 운영 적용 (Apply)
 * POST /api/v1/scenario/{module}/apply
 * @param {string} mod     - 모듈 코드
 * @param {object} payload - { requestId, scenarioId, releaseName, releaseDesc }
 */
const apply = async (mod, payload) => {
  const body = {
    request_id:   payload.requestId,
    scenario_id:  payload.scenarioId,
    release_name: payload.releaseName ?? "AI 시나리오 릴리즈",
    release_desc: payload.releaseDesc ?? "",
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/apply`, body);
  return res.data;
};

/**
 * AI Agent 챗봇
 * POST /api/v1/scenario/{module}/chat
 * @param {string} mod        - 모듈 코드
 * @param {object} ctx        - { tab, current_step, plan_ver_id, is_follow_up, step_label }
 * @param {string} message    - 사용자 메시지
 * @param {object} [session]  - { sessionId } (대화 연속성). user_id 는 서버 인증이 채운다.
 * @returns {{ reply: string, actions: list }}
 */
const chat = async (mod, ctx, message, session = {}) => {
  // ── 실서버 호출: context 필드명으로 전송 + session_id 포함 ────────────────
  const body = {
    context:    ctx,             // (주의) 'ctx' → 'context' (백엔드 DpChatRequest 필드명)
    message,
    session_id: session.sessionId ?? "",
    language:   getAgentLanguage(),
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/chat`, body);
  return res.data;
};

/**
 * AI Agent 챗봇(스트리밍) — 도구 진행과 텍스트를 실시간 수신
 * POST /api/v1/scenario/{module}/chat/stream
 *
 * 이벤트: start → (tool_start | tool_done | delta)* → result → done  (예외 시 error)
 *   delta  : { type, text } — 말풍선에 이어붙일 텍스트 조각
 *   result : chat()의 반환값과 동일한 { reply, actions, choices, expert_cases, ... }.
 *            **최종 정본**이므로 호출측은 델타 누적본을 이 값으로 치환한다
 *            (액션/선택지에 따라 본문이 사후 치환되거나 비워질 수 있다).
 *
 * ⚠️ fast-path(결정형 처리)로 끝나는 턴은 delta 가 하나도 없이 start → result → done 만 온다.
 *
 * @param {object} [handlers] - { onEvent(evt), signal }
 * @returns {Promise<void>} 스트림 종료 시 resolve
 */
const chatStream = async (mod, ctx, message, session = {}, { onEvent, signal } = {}) => {
  const body = {
    context:    ctx,
    message,
    session_id: session.sessionId ?? "",
    language:   getAgentLanguage(),
  };
  await _openSseStream(`${BASE}/${_mod(mod)}/chat/stream`, body, { onEvent, signal });
};

/**
 * AI Agent 챗봇 중지 — 실행 중인 턴에 취소 신호를 보낸다.
 * POST /api/v1/scenario/{module}/chat/abort
 *
 * ⚠️ SSE fetch 를 abort 하는 것만으로는 서버가 멈추지 않는다(LLM 이 끝까지 생성되고 과금된다).
 *    이 요청이 실제 취소를 담당한다 — 도구 실행으로 SSE 프레임이 끊긴 구간에서도 도달한다.
 *
 * @param {string} mod       - 모듈 코드
 * @param {object} [session] - { sessionId } — chat/stream 과 **같은 값**이어야 대상이 특정된다
 * @returns {{ cancelled: boolean, released: boolean }} released=false 면 아직 도구 실행 중일 수 있다
 */
const chatAbort = async (mod, session = {}) => {
  const body = {
    session_id: session.sessionId ?? "",
  };
  // 서버가 락 해제를 최대 1.5초 기다렸다 응답한다 — 그보다 넉넉히 잡되 길게 매달리지 않는다.
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/chat/abort`, body, { timeout: 5000 });
  return res.data;
};

/**
 * 전 모듈(all) 조회 규약 — Java 게이트웨이가 경로의 {module} 세그먼트를 라우팅표
 * (dp/ip/rp/mp/bf)로 검증하므로 'all' 을 경로에 쓰면 통과하지 못한다.
 * 경로는 항상 실제 모듈(dp 고정)로 두고 scope=all 쿼리로 전 모듈을 표현한다.
 */
const _isAll = (mod) => String(mod ?? "").toLowerCase() === "all";
const _modPath = (mod) => (_isAll(mod) ? "dp" : _mod(mod));
const _scopeParams = (mod, params = {}) => (_isAll(mod) ? { ...params, scope: "all" } : params);

/**
 * 챗 세션 목록 (히스토리) — 사용자 전역. mod='all'(권장)이면 전 모듈, module_cd 는
 * 세션의 마지막 대화 모듈. tenant/user 는 Java 가 주입한다.
 * GET /api/v1/scenario/{module}/chat/sessions[?scope=all]
 * @returns {{ sessions: [{ session_id, module_cd, title, last_ts, created_ts }] }}
 */
const chatSessionList = async (mod) => {
  const res = await _createInstance().get(`${BASE}/${_modPath(mod)}/chat/sessions`,
    { params: _scopeParams(mod), timeout: 15000 });
  return res.data;
};

/**
 * 챗 세션 대화 복원 — 표시용 메시지 목록(u/a, actions 는 spent, consulted 칩 포함).
 * GET /api/v1/scenario/{module}/chat/sessions/{sid}/messages
 * @returns {{ session_id, messages: [{ role, text, seq?, actions?, expertCases?, customDraft?, consulted? }] }}
 */
const chatSessionMessages = async (mod, sessionId) => {
  const res = await _createInstance().get(
    `${BASE}/${_mod(mod)}/chat/sessions/${encodeURIComponent(sessionId)}/messages`, { timeout: 15000 });
  return res.data;
};

/**
 * 챗 세션 삭제 (소프트) — 본인 세션만.
 * DELETE /api/v1/scenario/{module}/chat/sessions/{sid}
 */
const chatSessionDelete = async (mod, sessionId) => {
  const res = await _createInstance().delete(
    `${BASE}/${_mod(mod)}/chat/sessions/${encodeURIComponent(sessionId)}`, { timeout: 10000 });
  return res.data;
};

/**
 * [관리자] 챗 트레이스 — 전 사용자 세션 목록 + 턴/피드백 집계
 * GET /api/v1/scenario/{module}/chat/trace/sessions   (mod='all' 이면 전 모듈)
 */
const chatTraceSessions = async (mod, { feedbackOnly = false, includeEval = false, limit = 50 } = {}) => {
  const res = await _createInstance().get(`${BASE}/${_modPath(mod)}/chat/trace/sessions`,
    { params: _scopeParams(mod, { feedback_only: feedbackOnly, include_eval: includeEval, limit }),
      timeout: 15000 });
  return res.data;
};

/**
 * [관리자] 챗 트레이스 — 세션의 턴 목록(지표 요약)
 * GET /api/v1/scenario/{module}/chat/trace/sessions/{sid}/turns
 */
const chatTraceTurns = async (mod, sessionId) => {
  const res = await _createInstance().get(
    `${BASE}/${_modPath(mod)}/chat/trace/sessions/${encodeURIComponent(sessionId)}/turns`,
    { timeout: 15000 });
  return res.data;
};

/**
 * [관리자] 챗 트레이스 — 턴 상세(SQL 트레이스·원시 이력·피드백)
 * GET /api/v1/scenario/{module}/chat/trace/turns/{turnId}
 * ⚠️ turnId 는 BIGINT — 호출부는 항상 String 으로 다룰 것(JS 안전 정수 초과).
 */
const chatTraceTurnDetail = async (mod, turnId) => {
  const res = await _createInstance().get(
    `${BASE}/${_modPath(mod)}/chat/trace/turns/${String(turnId)}`, { timeout: 15000 });
  return res.data;
};

/**
 * [관리자] 챗 트레이스 — 최근 피드백 목록('싫어요 모아보기' 진입점)
 * GET /api/v1/scenario/{module}/chat/trace/feedback
 */
const chatTraceFeedback = async (mod, { limit = 50 } = {}) => {
  const res = await _createInstance().get(`${BASE}/${_modPath(mod)}/chat/trace/feedback`,
    { params: _scopeParams(mod, { limit }), timeout: 15000 });
  return res.data;
};

// ── [관리자] LLM 토큰 사용량 ─────────────────────────────────────────────────────
// 빈 값은 쿼리에서 뺀다 — 백엔드가 "필터 없음"으로 해석하도록.
// 일 경계는 브라우저 타임존으로 자른다 — 공용 고급 필터(AdvancedFilter)가 보내는 값과 같다
const _browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};
const _usageParams = (params = {}) =>
  Object.fromEntries(Object.entries({ tz: _browserTz(), ...params })
    .filter(([, v]) => v !== undefined && v !== null && v !== ""));

/** GET /api/v1/scenario/usage/llm/summary — KPI·일별 추이·기능/모듈/사용자/모델별 합계 */
const llmUsageSummary = async (params) => {
  const res = await _createInstance().get(`${BASE}/usage/llm/summary`,
    { params: _usageParams(params), timeout: 60000 });
  return res.data;
};

/** GET /api/v1/scenario/usage/llm/calls — 호출 명세(페이지) / export=true 면 상한까지 일괄 */
const llmUsageCalls = async (params) => {
  const res = await _createInstance().get(`${BASE}/usage/llm/calls`,
    { params: _usageParams(params), timeout: 120000 });
  return res.data;
};

/** GET /api/v1/scenario/usage/llm/models — 사용 이력이 있는 모델과 단가 등록 여부 */
const llmUsageModels = async () => {
  const res = await _createInstance().get(`${BASE}/usage/llm/models`,
    { params: _usageParams(), timeout: 15000 });
  return res.data;
};

/** GET /api/v1/scenario/usage/llm/prices — 활성 단가 목록(적용 종료일은 서버 도출) */
const llmUsagePrices = async () => {
  const res = await _createInstance().get(`${BASE}/usage/llm/prices`, { timeout: 15000 });
  return res.data;
};

/** GET /api/v1/scenario/usage/llm/prices/sources — 제공사별 공식 가격 데이터 지원 여부와 취득 조건 */
const llmUsagePriceSources = async () => {
  const res = await _createInstance().get(`${BASE}/usage/llm/prices/sources`, { timeout: 60000 });
  return res.data;
};

/** GET /api/v1/scenario/usage/llm/prices/suggest — 공식 가격 데이터에서 받은 단가 후보(저장하지 않는다) */
const llmUsagePricesSuggest = async (params) => {
  const res = await _createInstance().get(`${BASE}/usage/llm/prices/suggest`,
    { params: _usageParams(params), timeout: 60000 });
  return res.data;
};

/** POST /api/v1/scenario/usage/llm/prices/save — id 없으면 등록, 있으면 ver_num 낙관적 잠금 수정 */
const llmUsagePricesSave = async (rows) => {
  const res = await _createInstance().post(`${BASE}/usage/llm/prices/save`, { rows }, { timeout: 15000 });
  return res.data;
};

/** POST /api/v1/scenario/usage/llm/prices/delete — 논리 삭제 */
const llmUsagePricesDelete = async (ids) => {
  const res = await _createInstance().post(`${BASE}/usage/llm/prices/delete`, { ids }, { timeout: 15000 });
  return res.data;
};

/** GET /api/v1/scenario/terms/catalog — 표준 용어와 테넌트 차분(호칭·뜻)을 합친 목록 */
const termCatalog = async (locale) => {
  const res = await _createInstance().get(`${BASE}/terms/catalog`, { params: locale ? { locale } : {}, timeout: 30000 });
  return res.data;
};

/** POST /api/v1/scenario/terms/save — id 없으면 등록, 있으면 ver_num 낙관적 잠금 수정 */
const termSave = async (rows) => {
  const res = await _createInstance().post(`${BASE}/terms/save`, { rows }, { timeout: 15000 });
  return res.data;
};

/** POST /api/v1/scenario/terms/delete — 논리 삭제(표준으로 되돌리기) */
const termDelete = async (ids) => {
  const res = await _createInstance().post(`${BASE}/terms/delete`, { ids }, { timeout: 15000 });
  return res.data;
};

/**
 * 답변 피드백 저장 (up/down + 사유)
 * POST /api/v1/scenario/{module}/chat/feedback
 * @param {object} payload - { sessionId, seq, rating: 'up'|'down', reason? }
 */
const chatFeedback = async (mod, payload) => {
  const body = {
    session_id: payload.sessionId,
    seq:        payload.seq,
    rating:     payload.rating,
    reason:     payload.reason ?? "",
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/chat/feedback`, body, { timeout: 10000 });
  return res.data;
};

/**
 * 의사결정 리포트 생성 (비교 결과 → AI 산문 리포트, 동기)
 * POST /api/v1/scenario/{module}/report
 * @param {string} mod     - 모듈 코드 (현재 BF 지원)
 * @param {object} payload - { requestId, language? }
 * @returns {{ available, title, meta, sections: [{key, heading, body}], source }}
 */
const report = async (mod, payload) => {
  const body = {
    request_id: payload.requestId,
    language:   payload.language ?? getAgentLanguage(),
  };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/report`, body, { timeout: 120000 });
  return res.data;
};

/**
 * 리포트 HTML → 선택 가능한 PDF(base64) 변환 (서버 Playwright 렌더)
 * POST /api/v1/scenario/{module}/report-pdf
 * @param {string} mod     - 모듈 코드 (현재 BF)
 * @param {object} payload - { html, filename }
 * @returns {{ filename, pdf_base64 }}
 */
const reportPdf = async (mod, payload) => {
  const body = { html: payload.html, filename: payload.filename || "report.pdf" };
  const res = await _createInstance().post(`${BASE}/${_mod(mod)}/report-pdf`, body, { timeout: 120000 });
  return res.data;
};

/**
 * 현황 조회: 모듈별 가장 최근 릴리즈(완료) 플랜 버전 조회
 * GET /api/v1/scenario/{module}/latest-version
 * BF  → z_bf_status seq_num=100, COMPLETED 기준
 * 기타 → z_plan_version status_cd='RELEASED' 기준
 * @param {string} mod - BF | DP | IP | RP | MP
 * @returns {{ found, id, name, releaseTs, module }}
 */
const getLatestVersion = async (mod) => {
  const res = await _createInstance().get(`${BASE}/${_mod(mod)}/latest-version`);
  return res.data;
};

/**
 * 확정 없이 남은 시나리오 플래닝 세트 조회.
 * GET /api/v1/scenario/{module}/pending-set
 * 화면 진입 시 이어서 할지 새로 시작할지 묻기 위해 호출한다.
 * resumable 은 서버가 판정한다 — 요청이 COMPLETED 일 때만 이어받을 수 있다.
 * @param {string} mod - DP | IP | RP | MP (BF 는 항상 found:false)
 * @returns {{ found, module, planVerIds, versionNames, requestId, requestStatus, createdTs, resumable }}
 */
const getPendingSet = async (mod) => {
  const res = await _createInstance().get(`${BASE}/${_mod(mod)}/pending-set`);
  return res.data;
};

/**
 * 남은 시나리오 플래닝 세트 폐기 — 화면의 "새로 시작".
 * DELETE /api/v1/scenario/{module}/pending-set
 * 지울 대상은 보내지 않는다. 서버가 호출 시점에 다시 산출한다.
 * @returns {{ module, deleted, skipped, notFound, requestsMarked }}
 */
const discardPendingSet = async (mod) => {
  const res = await _createInstance().delete(`${BASE}/${_mod(mod)}/pending-set`);
  return res.data;
};

/**
 * 시나리오 분석 요청 이력 (전 모듈 공용).
 * 현황 빈 상태 패널의 '최근 실행' 요약이 사용한다 — 모듈마다 다른 서비스를 쓰지 않도록
 * 표준 v1 엔드포인트 하나로 통일했다.
 * @param {string} mod - BF | DP | IP | RP | MP
 * @param {number} limit - 최근 N건 (기본 1 — 요약 한 줄용)
 * @returns {{ history: Array<{request_id, status_cd, created_ts, ...}> }}
 */
const getHistory = async (mod, limit = 1) => {
  const res = await _createInstance().get(`${BASE}/${_mod(mod)}/history`, { params: { limit } });
  return res.data;
};

// ── export ────────────────────────────────────────────────────────────────────
const scenarioService = {
  getHistory,
  getPendingSet,
  discardPendingSet,
  suggest,
  suggestStream,
  analyze,
  analyzeStream,
  compareItemFacts,
  getResult,
  confirm,
  compareBfFeatures,
  apply,
  applyTransform,
  chat,
  chatStream,
  chatAbort,
  chatSessionList,
  chatSessionMessages,
  chatSessionDelete,
  chatFeedback,
  chatTraceSessions,
  chatTraceTurns,
  chatTraceTurnDetail,
  chatTraceFeedback,
  llmUsageSummary,
  llmUsageCalls,
  llmUsageModels,
  llmUsagePrices,
  llmUsagePriceSources,
  llmUsagePricesSuggest,
  llmUsagePricesSave,
  llmUsagePricesDelete,
  termCatalog,
  termSave,
  termDelete,
  report,
  reportPdf,
  getBfApprovedSignals,
  getBfFactorPreview,
  getBfIssueAnalysis,
  getBfInitialState,
  getDpInitialState,
  getFpInitialState,
  getIpInitialState,
  getRpInitialState,
  getRpScenarios,
  getScenarios,
  getMpInitialState,
  getLatestVersion,
  customGenerate,
  saveCustomScenario,
  listCustomScenarios,
  deleteCustomScenario,
  listFavoriteScenarios,
  setFavoriteScenario,
};

export default scenarioService;
